import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { inputCls, btnPrimary, cardCls, Field } from "@/components/shared";

const AuthCard = ({ title, subtitle, children, testId }) => (
  <div className="min-h-screen flex items-center justify-center px-6 py-12 relative z-10" data-testid={testId}>
    <div className={`${cardCls} w-full max-w-md p-8 sm:p-10 fade-up`}>
      <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1 text-sm text-t-muted hover:text-t-ink mb-8"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
      <h1 className="font-serif-display text-4xl font-semibold">{title}</h1>
      <p className="text-sm text-t-muted mt-2 mb-8">{subtitle}</p>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard testId="forgot-password-page" title="Forgot your password?" subtitle="Enter your email and we'll send you a link to choose a new one.">
      {sent ? (
        <div data-testid="forgot-password-sent" className="flex gap-3 rounded-xl border border-t-c2-line bg-t-c2-soft/70 p-4">
          <MailCheck className="h-5 w-5 text-t-c2 shrink-0 mt-0.5" />
          <p className="text-sm">If an account exists for <span className="font-semibold">{email}</span>, a reset link is on its way. It expires in 60 minutes — check your spam folder too.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email"><input data-testid="forgot-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" /></Field>
          {error && <p data-testid="forgot-error" className="text-sm text-t-danger">{error}</p>}
          <button type="submit" disabled={busy} data-testid="forgot-submit-button" className={`${btnPrimary} w-full py-3`}>{busy ? "Sending..." : "Send reset link"}</button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ a: "", b: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setError("The two passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.a });
      toast.success("Password updated — please sign in");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard testId="reset-password-page" title="Choose a new password" subtitle="Pick something at least 6 characters long.">
      {!token ? (
        <p data-testid="reset-missing-token" className="text-sm text-t-danger">This link is missing its reset code. <Link to="/forgot-password" className="underline">Request a new link</Link>.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="New password"><input data-testid="reset-password-input" type="password" minLength={6} required className={inputCls} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} /></Field>
          <Field label="Confirm new password"><input data-testid="reset-password-confirm-input" type="password" minLength={6} required className={inputCls} value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} /></Field>
          {error && (
            <p data-testid="reset-error" className="text-sm text-t-danger">
              {error} {error.includes("expired") && <Link to="/forgot-password" className="underline">Request a new link</Link>}
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="reset-submit-button" className={`${btnPrimary} w-full py-3`}>{busy ? "Saving..." : "Save new password"}</button>
        </form>
      )}
    </AuthCard>
  );
}
